import React, { useEffect, useState } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet,
  TextInput, Modal, ActivityIndicator, Linking,
} from 'react-native';
import { useAuth } from '../../hooks/useAuth';
import { getDocuments, createDocument, updateDocument, deleteDocument } from '../../services/documentService';
import { Document } from '../../types';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import { useLanguage } from '../../contexts/LanguageContext';

export default function DocumentsScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<Document | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [docUrl, setDocUrl] = useState('');

  const role = appUser?.role ?? 'guest';
  const canView = role !== 'guest';
  const canManage = role === 'admin' || role === 'manager';

  const load = async () => {
    setLoading(true);
    const docs = await getDocuments();
    setDocuments(docs);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setDocTitle('');
    setDocDescription('');
    setDocUrl('');
    setModalVisible(true);
  };

  const openEdit = (doc: Document) => {
    setEditing(doc);
    setDocTitle(doc.title);
    setDocDescription(doc.description ?? '');
    setDocUrl(doc.url);
    setModalVisible(true);
  };

  const handleSave = async () => {
    if (!docTitle.trim() || !docUrl.trim()) {
      CrossPlatformAlert.alert(t('common.validation'), t('documents.validationMessage'));
      return;
    }
    try {
      if (editing) {
        await updateDocument(editing.id, docTitle.trim(), docUrl.trim(), docDescription.trim());
      } else {
        await createDocument(docTitle.trim(), docUrl.trim(), docDescription.trim());
      }
      setModalVisible(false);
      load();
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    }
  };

  const handleDelete = (doc: Document) => {
    CrossPlatformAlert.alert(t('common.delete'), t('documents.confirmDelete', doc.title), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'), style: 'destructive', onPress: async () => {
          await deleteDocument(doc.id);
          load();
        },
      },
    ]);
  };

  const handleOpen = (url: string) => {
    Linking.openURL(url);
  };

  if (!canView) {
    return <View style={styles.center}><Text style={styles.noAccess}>{t('common.noAccess')}</Text></View>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('documents.title')}</Text>
          {canManage && (
            <TouchableOpacity style={styles.addBtn} onPress={openCreate}>
              <Text style={styles.addBtnText}>{t('common.add')}</Text>
            </TouchableOpacity>
          )}
        </View>

        {loading ? (
          <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
        ) : (
          <FlatList
            data={documents}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.docCard}
                onPress={() => handleOpen(item.url)}
                activeOpacity={0.7}
              >
                <View style={styles.docInfo}>
                  <Text style={styles.docTitle}>{item.title}</Text>
                  {item.description ? (
                    <Text style={styles.docDescription}>{item.description}</Text>
                  ) : null}
                </View>
                <View style={styles.docActions}>
                  <TouchableOpacity
                    style={styles.openBtn}
                    onPress={(e) => { e.stopPropagation(); handleOpen(item.url); }}
                  >
                    <Text style={styles.openBtnText}>{t('common.open')}</Text>
                  </TouchableOpacity>
                  {canManage && (
                    <>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); openEdit(item); }}
                        style={styles.editBtn}
                      >
                        <Text style={styles.editText}>{t('common.edit')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); handleDelete(item); }}
                        style={styles.deleteBtn}
                      >
                        <Text style={styles.deleteText}>{t('common.del')}</Text>
                      </TouchableOpacity>
                    </>
                  )}
                </View>
              </TouchableOpacity>
            )}
            ListEmptyComponent={<Text style={styles.empty}>{t('documents.noItems')}</Text>}
          />
        )}
      </View>

      <Modal visible={modalVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {editing ? t('documents.editTitle') : t('documents.addTitle')}
            </Text>
            <TextInput
              style={styles.input}
              placeholder={t('documents.titlePlaceholder')}
              value={docTitle}
              onChangeText={setDocTitle}
            />
            <TextInput
              style={styles.input}
              placeholder={t('common.descriptionPlaceholder')}
              value={docDescription}
              onChangeText={setDocDescription}
            />
            <TextInput
              style={styles.input}
              placeholder={t('documents.urlPlaceholder')}
              value={docUrl}
              onChangeText={setDocUrl}
              keyboardType="url"
              autoCapitalize="none"
            />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setModalVisible(false)}>
                <Text style={styles.cancelText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave}>
                <Text style={styles.saveText}>{t('common.save')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0',
  },
  title: { flex: 1, fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  addBtn: { backgroundColor: '#2563EB', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  addBtnText: { color: '#FFF', fontWeight: '600' },
  docCard: {
    backgroundColor: '#FFF', margin: 8, borderRadius: 12, padding: 16,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05, shadowRadius: 4, elevation: 1,
  },
  docInfo: { flex: 1, marginRight: 8 },
  docTitle: { fontSize: 16, fontWeight: '600', color: '#1E293B' },
  docDescription: { fontSize: 13, color: '#64748B', marginTop: 4 },
  docActions: { flexDirection: 'row', gap: 8 },
  openBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 },
  openBtnText: { color: '#2563EB', fontSize: 13, fontWeight: '600' },
  editBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6 },
  editText: { color: '#2563EB', fontSize: 12, fontWeight: '600' },
  deleteBtn: { backgroundColor: '#FEF2F2', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6 },
  deleteText: { color: '#EF4444', fontSize: 12, fontWeight: '600' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 40, fontSize: 16, padding: 16 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalContent: { backgroundColor: '#FFF', borderRadius: 20, padding: 24, width: '100%', maxWidth: 480 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#1E293B', marginBottom: 16 },
  input: { borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, padding: 12, fontSize: 15, marginBottom: 12 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' },
  cancelText: { color: '#64748B', fontWeight: '600' },
  saveBtn: { backgroundColor: '#2563EB', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  saveText: { color: '#FFF', fontWeight: '600' },
});
